import React, { useState, useCallback, useEffect, useRef } from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ClearIcon from '@mui/icons-material/Clear';
import DateRangeComponent from '../DateRangePicker';
import LoaderDotted from '../LoaderDotted/LoaderDotted';
import {
  getCurrentUser,  
  getRoomType,
  getRoomRatePlan,
  getEmptyRoomNumber,
  getRoomInformationData,
  getGroupBookingDetails,
  changeInfoForGroupCheckIn
} from '../../api-config/config';
// import { getGroupBookingDetails } from '../../api-config/bill.api';
import { formateDateInApiSupport, isEqualOrAfter } from '../../utils/formatTime';
import { calNoOfNights } from "../../utils/formatNumber";
import moment from "moment";
import './EditGroupReservation.css';

const getTaxRateForBase = (basePrice: number, nights: number, roomCount = 1) => {
  const pricePerRoomPerNight =
    basePrice / (Math.max(nights, 1) * Math.max(roomCount, 1));
  return pricePerRoomPerNight > 7500 ? 18 : 5;
};

// ==================== TYPES ====================
interface RoomData {
  id: number;
  booking_id?: number;
  room_booking_id?: number;
  booking_status?: string;
  is_new_booking?: boolean;
  room_type_id: number;
  room_ids: number[];
  adults_per_room: number;
  rate_plan_id: number;
  rate_plan_amount?: number;
  no_of_rooms: number;
  first_name: string;
  last_name: string;
  email: string;
  mobile: string;
  children: number;
  price: string;
  final_price: string;
  total_price: string;
  tax_amount: number;
  use_reverse_price?: boolean;
}

interface RoomTypeOption {
  id: number;
  room_type_name: string;
  rooms?: any[];
}

interface RatePlanOption {
  id: number;
  name: string;
  included: string;
}

interface AvailableRoom {
  id: number;
  room_name: string;
  room_number?: string;
  pms_room_no?: string;
  room_no?: string;
  room_title?: string;
  pms_room_name?: string;
}

const getRoomFromTypeList = (roomId: number, roomTypeList: RoomTypeOption[] = []) => {
  for (const roomType of roomTypeList) {
    const match = roomType.rooms?.find((room: any) => Number(room.id) === Number(roomId));
    if (match) return match;
  }
  return null;
};

// Builds "Room Name 101" using room type list when API only returns the type name.
const formatRoomLabel = (roomData?: any, roomTypeList: RoomTypeOption[] = []) => {
  if (!roomData) return "";

  const typeListRoom = roomData.id
    ? getRoomFromTypeList(Number(roomData.id), roomTypeList)
    : null;

  const roomName = (
    typeListRoom?.room_name ||
    roomData.room_name ||
    roomData.room_title ||
    roomData.pms_room_name ||
    ""
  ).toString();
  const roomNumber = (
    typeListRoom?.room_number ||
    roomData.room_number ||
    roomData.pms_room_no ||
    roomData.room_no ||
    ""
  ).toString();

  if (roomName && roomNumber && !roomName.includes(roomNumber)) {
    return `${roomName} ${roomNumber}`;
  }

  return roomName || roomNumber || (roomData.id ? roomData.id.toString() : "");
};

interface DateRange {
  startDate: Date;
  endDate: Date;
  key: string;
}

interface GroupBookingInfo {
  group_booking_id: number;
  main_booking_id: number;
  main_guest_id: number;
  hotel_code: number;
}

type RoomValidationErrors = Record<number, Record<string, string>>;

interface Props {
  groupBookingId: number;
  handleClose?: () => void;
  onFormSubmitted?: (response?: any, options?: { keepOpen?: boolean }) => void;
}

const createDefaultRoomData = (id: number): RoomData => ({
  id,
  is_new_booking: true,
  booking_status: "pending",
  room_type_id: 0,
  room_ids: [],
  adults_per_room: 1,
  rate_plan_id: 0,
  no_of_rooms: 1,
  first_name: "",
  last_name: "",
  email: "",
  mobile: "",
  children: 0,
  price: "",
  final_price: "",
  total_price: "",
  tax_amount: 0,
  use_reverse_price: false,
});

const EditGroupReservation: React.FC<Props> = ({
  groupBookingId,
  handleClose,
  onFormSubmitted,
}) => {
  const loggedUser = getCurrentUser();

  // State for group booking info
  const [groupBookingInfo, setGroupBookingInfo] = useState<GroupBookingInfo | null>(null);

  // State for date range
  const [range, setRange] = useState<DateRange>({
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
  });

  // State for rooms and related data
  const [rooms, setRooms] = useState<RoomData[]>([]);
  const [nextRoomId, setNextRoomId] = useState(2);
  const [roomTypeList, setRoomTypeList] = useState<RoomTypeOption[]>([]);
  const [roomRatePlans, setRoomRatePlans] = useState<Record<number, RatePlanOption[]>>({});
  const [availableRooms, setAvailableRooms] = useState<Record<number, AvailableRoom[]>>({});
  const [roomInfoData, setRoomInfoData] = useState<any>(null);

  // State for UI interactions
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [searchTerms, setSearchTerms] = useState<Record<number, string>>({});
  const [dropdownStates, setDropdownStates] = useState<Record<number, boolean>>({});
  const [validationErrors, setValidationErrors] = useState<RoomValidationErrors>({});
  const dropdownRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const hydratedOptionsRef = useRef<Record<number, number>>({});

  // Fetch group booking details
  const fetchGroupBookingDetails = useCallback(async () => {
    if (!groupBookingId) {
      console.error("Group booking ID not provided");
      setIsFetching(false);
      return;
    }

    try {
      setIsFetching(true);
      const response = await getGroupBookingDetails(groupBookingId);

      if (response?.status_message === "success" && response?.data) {
        const data = response.data;

        // Set group booking info header
        setGroupBookingInfo({
          group_booking_id: data.group_booking_id,
          main_booking_id: data.main_booking_id,
          main_guest_id: data.main_guest_id,
          hotel_code: data.hotel_code,
        });

        // Set date range
        setRange({
          startDate: moment(data.from_date).startOf("day").toDate(),
          endDate: moment(data.to_date).startOf("day").toDate(),
          key: "selection",
        });

        // Convert bookings to room state structure
        if (data.bookings && Array.isArray(data.bookings)) {
          let maxRoomId = 1;
          const selectedRoomsByType: Record<number, AvailableRoom[]> = {};
          const selectedRatePlansByRoom: Record<number, RatePlanOption[]> = {};
          const activeBookings = data.bookings.filter((booking: { booking_status?: string }) =>
            (booking.booking_status || "").trim().toLowerCase() !== "cancelled"
          );

          data.bookings.forEach((booking: { booking_id?: number; id?: number }) => {
            const bookingId = booking.booking_id || booking.id || 0;
            if (bookingId >= maxRoomId) {
              maxRoomId = bookingId + 1;
            }
          });

          const convertedRooms: RoomData[] = activeBookings.map((booking: any) => {
            const roomBookingInfoList = Array.isArray(booking.room_booking_info)
              ? booking.room_booking_info
              : booking.room_booking_info
                ? [booking.room_booking_info]
                : [];
            const roomBookingInfo = roomBookingInfoList[0] || {};
            const roomTypeId = roomBookingInfo.pms_room_type_id || roomBookingInfo.room_type_id || 0;
            const selectedRoomId = roomBookingInfo.pms_room_id || roomBookingInfo.room_id || 0;
            const selectedRoomNumber = (
              roomBookingInfo.room_number ||
              roomBookingInfo.pms_room_no ||
              roomBookingInfo.room_no ||
              ""
            ).toString();
            const selectedRoomName = (
              roomBookingInfo.room_name ||
              roomBookingInfo.room_title ||
              roomBookingInfo.pms_room_name ||
              ""
            ).toString();
            const selectedRoomDisplayName = formatRoomLabel({
              room_name: selectedRoomName,
              room_number: selectedRoomNumber,
              id: selectedRoomId,
            });
            const ratePlanId = roomBookingInfo.pms_rate_plan_id || roomBookingInfo.rate_plan_id || 0;
            const ratePlanName = roomBookingInfo.pms_rate_plan_name || roomBookingInfo.rate_plan_name || "";
            const totalWithoutTax = roomBookingInfo.total_without_tax ?? booking.total_without_tax ?? roomBookingInfo.total_sale_amount ?? booking.total_sale_amount ?? "";
            const taxAmount = roomBookingInfo.tax_amount ?? booking.tax_amount ?? 0;
            const roomGrossAmount = roomBookingInfo.gross_amount || roomBookingInfo.total_price_with_tax || "";
            const grossAmount = roomGrossAmount || ((Number(totalWithoutTax) || 0) + (Number(taxAmount) || 0)) || booking.gross_amount || "";
            const bookingId = booking.booking_id || booking.id;

            const roomData: RoomData = {
              id: bookingId,
              booking_id: bookingId,
              room_booking_id: roomBookingInfo.room_booking_id || roomBookingInfo.id,
              booking_status: booking.booking_status || "pending",
              room_type_id: roomTypeId,
              room_ids: selectedRoomId ? [selectedRoomId] : [],
              adults_per_room: roomBookingInfo.no_of_adults || booking.no_of_adults || 0,
              rate_plan_id: ratePlanId,
              rate_plan_amount: roomBookingInfo.rate_plan_amount || 0,
              no_of_rooms: roomBookingInfo.no_of_rooms || 1,
              first_name: booking.first_name || "",
              last_name: booking.last_name || "",
              email: booking.email || "",
              mobile: booking.mobile_number || "",
              children: roomBookingInfo.no_of_children || booking.no_of_children || 0,
              price: totalWithoutTax.toString(),
              final_price: grossAmount.toString(),
              total_price: totalWithoutTax.toString(),
              tax_amount: taxAmount,
            };

            if (roomTypeId && selectedRoomId && selectedRoomDisplayName) {
              selectedRoomsByType[roomTypeId] = [
                ...(selectedRoomsByType[roomTypeId] || []),
                {
                  id: selectedRoomId,
                  room_name: selectedRoomDisplayName,
                  room_number: selectedRoomNumber,
                },
              ];
            }

            if (ratePlanId && ratePlanName) {
              selectedRatePlansByRoom[bookingId] = [
                {
                  id: ratePlanId,
                  name: ratePlanName,
                  included: roomBookingInfo.pms_rate_plan_included || roomBookingInfo.rate_plan_included || "",
                },
              ];
            }

            return roomData;
          });

          setRooms(convertedRooms);
          setNextRoomId(maxRoomId);
          setAvailableRooms(prev => {
            const updated = { ...prev };
            Object.entries(selectedRoomsByType).forEach(([roomTypeId, selectedRooms]) => {
              const existingRooms = updated[Number(roomTypeId)] || [];
              updated[Number(roomTypeId)] = [
                ...existingRooms,
                ...selectedRooms.filter(selectedRoom =>
                  !existingRooms.some(existingRoom => existingRoom.id === selectedRoom.id)
                ),
              ];
            });
            return updated;
          });
          setRoomRatePlans(prev => ({
            ...prev,
            ...selectedRatePlansByRoom,
          }));
        }
      } else {
        console.error("Failed to fetch group booking details:", response);
      }
    } catch (error) {
      console.error("Error fetching group booking details:", error);
    } finally {
      setIsFetching(false);
    }
  }, [groupBookingId]);

  // Fetch room types
  const fetchRoomTypes = useCallback(async () => {
    if (!loggedUser?.hotel_code) return;

    try {
      const res = await getRoomType(loggedUser.hotel_code, 1);
      if (res?.status_message === "success") {
        setRoomTypeList(res.data);
      }
    } catch (error) {
      console.error("Error fetching room types:", error);
    }
  }, [loggedUser?.hotel_code]);

  // Fetch rate plans
  const fetchRatePlans = useCallback(async (roomId: number, roomTypeId: number) => {
    if (!loggedUser?.hotel_code || !roomTypeId) return;

    try {
      const response = await getRoomRatePlan({
        hotel_code: loggedUser.hotel_code,
        room_type_id: roomTypeId,
      });
      const ratePlansForRoomType = (response?.data || []).filter(
        (plan: RatePlanOption, index: number, plans: RatePlanOption[]) =>
          plans.findIndex(existingPlan => existingPlan.id === plan.id) === index
      );
      setRoomRatePlans(prev => ({
        ...prev,
        [roomId]: ratePlansForRoomType,
      }));
    } catch (error) {
      console.error("Error fetching rate plans:", error);
    }
  }, [loggedUser?.hotel_code]);

  // Fetch available rooms
  const fetchAvailableRooms = useCallback(async (roomTypeId: number) => {
    if (!loggedUser?.hotel_code || !roomTypeId) return;

    try {
      const body = {
        from_date: formateDateInApiSupport(range.startDate),
        to_date: formateDateInApiSupport(range.endDate),
        pms_hotel_code: loggedUser.hotel_code,
        room_type_id: roomTypeId,
      };

      const response = await getEmptyRoomNumber(body);
      const emptyRooms = (response?.data || []).map((room: any) => ({
        ...room,
        room_name: formatRoomLabel(room, roomTypeList),
      }));
      setAvailableRooms(prev => ({
        ...prev,
        [roomTypeId]: [
          ...emptyRooms,
          ...(prev[roomTypeId] || []).filter(prevRoom =>
            !emptyRooms.some((room: AvailableRoom) => room.id === prevRoom.id)
          ),
        ],
      }));
    } catch (error) {
      console.error("Error fetching available rooms:", error);
    }
  }, [loggedUser?.hotel_code, range.startDate, range.endDate, roomTypeList]);

  // Fetch room information data
  const fetchRoomInformation = useCallback(async (adults: number, children = 0) => {
    if (!loggedUser?.hotel_code) return;

    try {
      const payload = {
        hotel_code: loggedUser.hotel_code,
        start_date: formateDateInApiSupport(range.startDate),
        end_date: formateDateInApiSupport(range.endDate),
        room_info: [
          {
            number_of_adults: adults,
            number_of_children: children,
            number_of_children_0_6: 0,
            number_of_children_6_12: 0,
          },
        ],
      };

      const response = await getRoomInformationData(payload);
      setRoomInfoData(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching room information:", error);
    }
  }, [loggedUser?.hotel_code, range.startDate, range.endDate]);

  // Calculate room price
  const calculateRoomPrice = useCallback((roomTypeId: number, room: RoomData, roomInfo: any) => {
    if (room.use_reverse_price) {
      return;
    }

    try {
      let basePrice = 0;
      let serviceCharge = 0;

      if (roomInfo?.rooms) {
        const roomTypeInfo = roomInfo.rooms.find((r: any) => r.room_type_id === roomTypeId);
        if (roomTypeInfo) {
          basePrice = roomTypeInfo.room_price?.total_price || 0;
          serviceCharge = roomTypeInfo.service_charge || 0;
        }
      }

      const nights = calNoOfNights(range.startDate, range.endDate);
      const selectedRoomCount = Math.max(room.room_ids.length, 1);
      const totalSalePrice = basePrice * selectedRoomCount;

      let ratePlanAmount = 0;
      if (room.rate_plan_id > 0) {
        const selectedPlan = roomRatePlans[room.id]?.find(p => p.id === room.rate_plan_id);
        if (selectedPlan) {
          ratePlanAmount = (selectedPlan as any).rate_amount || 0;
        }
      }

      const finalSalePrice = totalSalePrice + (ratePlanAmount * room.adults_per_room * nights);
      const roomServiceCharge = (finalSalePrice * serviceCharge) / 100;
      const priceWithService = finalSalePrice + roomServiceCharge;

      const roomCount = Math.max(room.room_ids.length, 1);
      const taxRate = getTaxRateForBase(priceWithService, nights, roomCount);
      const tax = (priceWithService * taxRate) / 100;
      const finalPrice = priceWithService + tax;

      setRooms(prev => prev.map(r => {
        if (r.id === room.id) {
          return {
            ...r,
            price: finalSalePrice.toString(),
            final_price: finalPrice.toString(),
            total_price: finalSalePrice.toString(),
            tax_amount: tax,
          };
        }
        return r;
      }));
    } catch (error) {
      console.error("Error calculating room price:", error);
    }
  }, [range.startDate, range.endDate, roomRatePlans]);

  const applyForwardPriceCalc = (room: RoomData): RoomData => {
    const basePrice = Math.max(0, Math.round(Number(room.total_price || room.price) || 0));
    const nights = calNoOfNights(range.startDate, range.endDate);
    const roomCount = Math.max(room.room_ids.length, 1);
    const taxRate = getTaxRateForBase(basePrice, nights, roomCount);
    const tax = Math.round((basePrice * taxRate) / 100);
    const finalPrice = basePrice + tax;

    return {
      ...room,
      final_price: finalPrice.toString(),
      tax_amount: tax,
      price: basePrice.toString(),
      total_price: basePrice.toString(),
    };
  };

  const applyReversePriceCalc = (room: RoomData): RoomData => {
    const finalPrice = Math.max(0, Math.round(Number(room.final_price) || 0));
    const nights = calNoOfNights(range.startDate, range.endDate);
    const roomCount = Math.max(room.room_ids.length, 1);

    if (finalPrice === 0) {
      return {
        ...room,
        price: "",
        total_price: "",
        tax_amount: 0,
        final_price: "",
      };
    }

    let basePrice = Math.round(finalPrice / 1.05);
    if (getTaxRateForBase(basePrice, nights, roomCount) === 18) {
      basePrice = Math.round(finalPrice / 1.18);
    }

    const tax = finalPrice - basePrice;

    return {
      ...room,
      price: basePrice.toString(),
      total_price: basePrice.toString(),
      tax_amount: tax,
      final_price: finalPrice.toString(),
    };
  };

  const handleReversePriceToggle = (roomId: number, enabled: boolean) => {
    setRooms(prev => prev.map(room => {
      if (room.id !== roomId) return room;

      const updatedRoom = { ...room, use_reverse_price: enabled };
      return enabled ? applyReversePriceCalc(updatedRoom) : applyForwardPriceCalc(updatedRoom);
    }));
  };

  // Initial setup - fetch group booking and room types
  useEffect(() => {
    fetchGroupBookingDetails();
    fetchRoomTypes();
  }, [fetchGroupBookingDetails, fetchRoomTypes]);

  // Hydrate dropdown options for rooms loaded from existing booking details
  useEffect(() => {
    rooms.forEach(room => {
      if (room.room_type_id > 0 && hydratedOptionsRef.current[room.id] !== room.room_type_id) {
        hydratedOptionsRef.current[room.id] = room.room_type_id;
        fetchRatePlans(room.id, room.room_type_id);
        fetchAvailableRooms(room.room_type_id);
      }
    });
  }, [rooms, fetchRatePlans, fetchAvailableRooms]);

  // Fetch room information when dates or first room guest count changes
  useEffect(() => {
    if (rooms.length > 0) {
      const firstRoom = rooms[0];
      fetchRoomInformation(firstRoom.adults_per_room || 1, firstRoom.children || 0);
    }
  }, [range.startDate, range.endDate, fetchRoomInformation]);

  // Handle click outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      Object.entries(dropdownRefs.current).forEach(([roomIdStr, dropdownRef]) => {
        if (dropdownRef && !dropdownRef.contains(event.target as Node)) {
          const roomId = parseInt(roomIdStr);
          setDropdownStates(prev => ({
            ...prev,
            [roomId]: false,
          }));
        }
      });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle room field changes
  const handleRoomFieldChange = (roomId: number, fieldName: string, value: any) => {
    setValidationErrors(prev => ({
      ...prev,
      [roomId]: {
        ...(prev[roomId] || {}),
        [fieldName]: "",
      },
    }));

    setRooms(prev => prev.map(room => {
      if (room.id !== roomId) return room;

      const updatedRoom = { ...room, [fieldName]: value };

      if (fieldName === 'room_type_id' && value > 0) {
        fetchRatePlans(roomId, value);
        fetchAvailableRooms(value);
        updatedRoom.room_ids = [];
        updatedRoom.rate_plan_id = 0;
        updatedRoom.rate_plan_amount = 0;
        updatedRoom.no_of_rooms = 1;
        updatedRoom.price = "";
        updatedRoom.total_price = "";
        updatedRoom.final_price = "";
        updatedRoom.tax_amount = 0;
        setSearchTerms(prevSearchTerms => ({
          ...prevSearchTerms,
          [roomId]: "",
        }));
        setDropdownStates(prevDropdownStates => ({
          ...prevDropdownStates,
          [roomId]: false,
        }));
        fetchRoomInformation(updatedRoom.adults_per_room || 1, updatedRoom.children || 0).then(data => {
          if (data) {
            calculateRoomPrice(value, updatedRoom, data);
          }
        });
      }

      if (fieldName === 'room_ids' && Array.isArray(value)) {
        updatedRoom.room_ids = value;
        updatedRoom.no_of_rooms = value.length || 1;
        if (updatedRoom.room_type_id) {
          fetchRoomInformation(updatedRoom.adults_per_room || 1, updatedRoom.children || 0).then(data => {
            if (data) {
              calculateRoomPrice(updatedRoom.room_type_id, updatedRoom, data);
            }
          });
        }
      }

      if (fieldName === 'rate_plan_id' && value > 0) {
        updatedRoom.rate_plan_id = value;
        if (updatedRoom.room_type_id) {
          fetchRoomInformation(updatedRoom.adults_per_room || 1, updatedRoom.children || 0).then(data => {
            if (data) {
              calculateRoomPrice(updatedRoom.room_type_id, updatedRoom, data);
            }
          });
        }
      }

      if (fieldName === 'total_price' && !updatedRoom.use_reverse_price) {
        if (value === "") {
          updatedRoom.price = "";
          updatedRoom.final_price = "";
          updatedRoom.tax_amount = 0;
          return updatedRoom;
        }
        return applyForwardPriceCalc(updatedRoom);
      }

      if (fieldName === 'final_price' && updatedRoom.use_reverse_price) {
        if (value === "") {
          updatedRoom.price = "";
          updatedRoom.total_price = "";
          updatedRoom.tax_amount = 0;
          return updatedRoom;
        }
        return applyReversePriceCalc(updatedRoom);
      }

      if ((fieldName === 'adults_per_room' || fieldName === 'children') && updatedRoom.room_type_id) {
        fetchRoomInformation(updatedRoom.adults_per_room, updatedRoom.children).then(data => {
          calculateRoomPrice(updatedRoom.room_type_id, updatedRoom, data);
        });
      }

      return updatedRoom;
    }));
  };

  // Handle add room
  const handleAddRoom = () => {
    const newRoom = createDefaultRoomData(nextRoomId);
    setRooms(prev => [...prev, newRoom]);
    setNextRoomId(prev => prev + 1);
  };

  const confirmCancellation = (): boolean => {
    const userResponse = window.prompt(
      "Do you want to continue for Cancellation? (Yes/No)"
    );
    if (userResponse === null) return false;
    return userResponse.trim().toLowerCase() === "yes";
  };

  const canCancelBookingStatus = (status?: string) => {
    const normalized = (status || "pending").trim().toLowerCase();
    return (
      normalized !== "check_in" &&
      normalized !== "check_out" &&
      normalized !== "cancelled"
    );
  };

  const canCheckInBookingStatus = (status?: string) => {
    return (status || "pending").trim().toLowerCase() === "pending";
  };

  const resolveBookingStatus = (room: RoomData, bookingStatus?: string) => {
    if (bookingStatus === "check_in") {
      return canCheckInBookingStatus(room.booking_status) ? "check_in" : (room.booking_status || "pending");
    }
    return bookingStatus || room.booking_status || "pending";
  };

  const getRoomAmountsForPayload = (room: RoomData) => {
    try {
      return getValidatedRoomAmounts(room);
    } catch {
      const totalWithoutTax = Number(room.total_price || room.price) || 0;
      const grossAmount = Number(room.final_price) || 0;
      const taxAmount = Number(room.tax_amount || grossAmount - totalWithoutTax) || 0;
      return { totalWithoutTax, grossAmount, taxAmount };
    }
  };

  const submitCancellation = async (
    roomsToSubmit: RoomData[],
    options?: { keepOpen?: boolean }
  ) => {
    setIsLoading(true);
    try {
      const payload = buildGroupCheckInPayload(undefined, roomsToSubmit);
      const response = await changeInfoForGroupCheckIn(payload);
      console.log("Group reservation cancel response:", response);

      if (response?.status_message === "failed") {
        alert(response?.message || "Cancellation failed");
        return false;
      }

      if (onFormSubmitted) {
        onFormSubmitted(response, options);
      } else if (!options?.keepOpen) {
        handleClose?.();
      }
      return true;
    } catch (error) {
      console.error("Error cancelling group reservation:", error);
      alert(error instanceof Error ? error.message : "Error cancelling room");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel a single room booking in the group
  const handleRemoveRoom = async (roomId: number) => {
    const roomToCancel = rooms.find(room => room.id === roomId);
    if (!roomToCancel) return;

    if (roomToCancel.is_new_booking || !roomToCancel.booking_id) {
      setRooms(prev => prev.filter(room => room.id !== roomId));
      setValidationErrors(prev => {
        const updated = { ...prev };
        delete updated[roomId];
        return updated;
      });
      setRoomRatePlans(prev => {
        const updated = { ...prev };
        delete updated[roomId];
        return updated;
      });
      return;
    }

    if (!canCancelBookingStatus(roomToCancel.booking_status)) {
      alert("This booking cannot be cancelled");
      return;
    }

    if (!confirmCancellation()) return;

    const existingRooms = rooms.filter(room => !room.is_new_booking);
    const updatedRooms = existingRooms.map(room =>
      room.id === roomId ? { ...room, booking_status: "cancelled" } : room
    );

    const cancelled = await submitCancellation(updatedRooms, { keepOpen: true });
    if (!cancelled) return;

    const remainingRooms = rooms.filter(room => room.id !== roomId);
    setRooms(remainingRooms);
    setValidationErrors(prev => {
      const updated = { ...prev };
      delete updated[roomId];
      return updated;
    });
    setRoomRatePlans(prev => {
      const updated = { ...prev };
      delete updated[roomId];
      return updated;
    });

    if (remainingRooms.length === 0) {
      handleClose?.();
    }
  };

  const handleCancelReservation = async () => {
    const existingRooms = rooms.filter(room => !room.is_new_booking);
    if (!existingRooms.length) {
      handleClose?.();
      return;
    }

    const cancellableRooms = existingRooms.filter(room =>
      canCancelBookingStatus(room.booking_status)
    );

    if (!cancellableRooms.length) {
      alert("No bookings available for cancellation");
      return;
    }

    if (!confirmCancellation()) return;

    const updatedRooms = existingRooms.map(room =>
      canCancelBookingStatus(room.booking_status)
        ? { ...room, booking_status: "cancelled" }
        : room
    );

    await submitCancellation(updatedRooms);
  };

  const hasCheckedInRoom = rooms.some(
    room => (room.booking_status || "").trim().toLowerCase() === "check_in"
  );

  // Handle date range change
  const handleDateRangeChange = (value: DateRange) => {
    if (!hasCheckedInRoom) {
      if (moment(value.startDate).isSame(value.endDate, "day")) {
        value.endDate = moment(value.startDate).add(1, "days").toDate();
      } else if (moment(value.endDate).isBefore(value.startDate, "day")) {
        value.startDate = moment(value.endDate).toDate();
        value.endDate = moment(value.startDate).add(1, "days").toDate();
      }
    } else if (
      moment(value.endDate).isBefore(value.startDate, "day") ||
      moment(value.startDate).isSame(value.endDate, "day")
    ) {
      value.endDate = moment(value.startDate).add(1, "days").toDate();
    }
    setRange(value);
  };

  const getValidatedRoomAmounts = (room: RoomData) => {
    const totalWithoutTaxValue = room.total_price || room.price;
    const grossAmountValue = room.final_price;
    const totalWithoutTax = Number(totalWithoutTaxValue);
    const grossAmount = Number(grossAmountValue);
    const taxAmount = Number(room.tax_amount || grossAmount - totalWithoutTax);

    if (
      totalWithoutTaxValue === "" ||
      grossAmountValue === "" ||
      !Number.isFinite(totalWithoutTax) ||
      !Number.isFinite(grossAmount) ||
      !Number.isFinite(taxAmount) ||
      totalWithoutTax < 0 ||
      grossAmount < 0 ||
      taxAmount < 0
    ) {
      throw new Error("Please enter valid total sale, total without tax, tax, and gross amounts for every room");
    }

    return {
      totalWithoutTax,
      grossAmount,
      taxAmount,
    };
  };

  const buildGroupCheckInPayload = (bookingStatus?: string, payloadRooms = rooms) => ({
    group_booking_id: groupBookingInfo?.group_booking_id || groupBookingId,
    from_date: formateDateInApiSupport(range.startDate),
    to_date: formateDateInApiSupport(range.endDate),
    bookings: payloadRooms.map(room => {
      const { totalWithoutTax, grossAmount, taxAmount } = getRoomAmountsForPayload(room);
      const noOfRooms = room.room_ids.length || room.no_of_rooms || 1;
      const roomBookingInfo = {
        ...(room.is_new_booking ? {} : { room_booking_id: room.room_booking_id }),
        room_id: room.room_ids[0] || 0,
        room_type_id: room.room_type_id,
        rate_plan_id: room.rate_plan_id,
        rate_plan_amount: room.rate_plan_amount || 0,
        no_of_pax: room.adults_per_room + room.children,
        no_of_adults: room.adults_per_room,
        no_of_children: room.children,
        no_of_rooms: noOfRooms,
        total_sale_amount: totalWithoutTax,
        total_without_tax: totalWithoutTax,
        tax_amount: taxAmount,
        gross_amount: grossAmount,
        discount_amount: 0,
        coupon_amount: 0,
      };

      return {
        ...(room.is_new_booking ? { is_new_booking: true } : { booking_id: room.booking_id || room.id }),
        from_date: formateDateInApiSupport(range.startDate),
        to_date: formateDateInApiSupport(range.endDate),
        booking_status: resolveBookingStatus(room, bookingStatus),
        first_name: room.first_name,
        last_name: room.last_name,
        email: room.email,
        mobile_number: room.mobile,
        phone_number: room.mobile,
        total_sale_amount: totalWithoutTax,
        total_without_tax: totalWithoutTax,
        tax_amount: taxAmount,
        gross_amount: grossAmount,
        room_booking_info: [roomBookingInfo],
      };
    }),
  });

  const scrollToFirstValidationError = (errors: RoomValidationErrors) => {
    const roomIds = Object.keys(errors);
    if (!roomIds.length) return;

    const firstRoomId = roomIds[0];
    const firstField = Object.keys(errors[Number(firstRoomId)])[0];

    setTimeout(() => {
      const fieldEl = document.querySelector(
        `[data-validation-field="${firstRoomId}-${firstField}"]`
      );
      if (fieldEl) {
        fieldEl.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      const roomEl = document.querySelector(`[data-room-id="${firstRoomId}"]`);
      roomEl?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  const validateRooms = () => {
    const errors: RoomValidationErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobileRegex = /^\d{10}$/;

    rooms.forEach(room => {
      const roomErrors: Record<string, string> = {};
      const priceValue = room.use_reverse_price
        ? room.final_price
        : (room.total_price || room.price);

      if (!room.room_type_id) roomErrors.room_type_id = "Please select room type";
      if (!room.room_ids.length) roomErrors.room_ids = "Please select room";
      if (!room.rate_plan_id) roomErrors.rate_plan_id = "Please select rate type";
      if (!room.adults_per_room) roomErrors.adults_per_room = "Please select adult";
      if (!room.first_name.trim()) roomErrors.first_name = "Please enter first name";
      if (!room.last_name.trim()) roomErrors.last_name = "Please enter last name";
      if (!room.email.trim()) {
        roomErrors.email = "Please enter email";
      } else if (!emailRegex.test(room.email.trim())) {
        roomErrors.email = "Please enter valid email";
      }
      if (!room.mobile.trim()) {
        roomErrors.mobile = "Please enter mobile number";
      } else if (!mobileRegex.test(room.mobile.trim())) {
        roomErrors.mobile = "Mobile number must be 10 digits";
      }
      if (
        priceValue === "" ||
        !Number.isFinite(Number(priceValue)) ||
        Number(priceValue) <= 0
      ) {
        if (room.use_reverse_price) {
          roomErrors.final_price = "Please enter valid final price";
        } else {
          roomErrors.total_price = "Please enter valid price";
        }
      }

      if (Object.keys(roomErrors).length) {
        errors[room.id] = roomErrors;
      }
    });

    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      scrollToFirstValidationError(errors);
      return false;
    }
    return true;
  };

  const handleSaveChanges = async (bookingStatus?: string) => {
    if (!groupBookingInfo?.group_booking_id && !groupBookingId) {
      console.error("Group booking ID not provided");
      return;
    }

    if (
      bookingStatus === "check_in" &&
      !isEqualOrAfter(moment().startOf("day"), moment(range.startDate).startOf("day"))
    ) {
      alert("Check-in is not allowed for future dates");
      return;
    }

    if (
      bookingStatus === "check_in" &&
      !rooms.some(room => canCheckInBookingStatus(room.booking_status))
    ) {
      alert("No bookings available for check-in");
      return;
    }

    if (!validateRooms()) {
      return;
    }

    setIsLoading(true);
    try {
      const payload = buildGroupCheckInPayload(bookingStatus);
      const response = await changeInfoForGroupCheckIn(payload);
      console.log("Group reservation update response:", response);

      if (response?.status_message === "failed") {
        alert(response?.message || "Update failed");
        return;
      }

      if (onFormSubmitted) {
        onFormSubmitted(response);
      } else if (handleClose) {
        handleClose();
      }
    } catch (error) {
      console.error("Error updating group reservation:", error);
      alert(error instanceof Error ? error.message : "Error updating group reservation");
    } finally {
      setIsLoading(false);
    }
  };

  // Render room form - reused from GroupReservationForm
  const renderRoomForm = (room: RoomData, index: number) => {
    const ratePlans = roomRatePlans[room.id] || [];
    const availableRoomsList = room.room_type_id ? (availableRooms[room.room_type_id] || []) : [];

    const searchTerm = searchTerms[room.id] || "";
    const isDropdownOpen = dropdownStates[room.id] || false;
    const canEditRoomAssignment = (room.booking_status || "pending").trim().toLowerCase() === "pending";
    const roomErrors = validationErrors[room.id] || {};

    const handleRoomSelection = (roomId: number) => {
      if (!canEditRoomAssignment) return;

      const isSelected = room.room_ids.includes(roomId);
      const updatedRoomIds = isSelected ? [] : [roomId];

      handleRoomFieldChange(room.id, 'room_ids', updatedRoomIds);
      setDropdownStates(prev => ({
        ...prev,
        [room.id]: false,
      }));
    };

    const handleClearAll = () => {
      if (!canEditRoomAssignment) return;

      handleRoomFieldChange(room.id, 'room_ids', []);
      setSearchTerms(prev => ({
        ...prev,
        [room.id]: "",
      }));
    };

    const getRoomDisplayName = (roomId: number) => {
      const cachedRoom = availableRoomsList.find(
        (room) => Number(room.id) === Number(roomId)
      );
      return formatRoomLabel({ ...cachedRoom, id: roomId }, roomTypeList) || String(roomId);
    };

    const selectedRoomIdsInOtherRows = rooms
      .filter(existingRoom => existingRoom.id !== room.id)
      .flatMap(existingRoom => existingRoom.room_ids);
    const filteredRooms = availableRoomsList.filter(availRoom => {
      const isSelectedInCurrentRow = room.room_ids.includes(availRoom.id);
      const isSelectedInOtherRow = selectedRoomIdsInOtherRows.includes(availRoom.id);
      return (
        (isSelectedInCurrentRow || !isSelectedInOtherRow) &&
        getRoomDisplayName(availRoom.id).toLowerCase().includes(searchTerm.toLowerCase())
      );
    });

    const selectedRoomNames = room.room_ids.map((roomId) => getRoomDisplayName(roomId));

    return (
      <div key={room.id} className="room-card" data-room-id={room.id}>
        <div className="room-content">
          <div className="room-header">
            <span className="room-header-text">Room {index + 1}</span>
            {canEditRoomAssignment && (
              <button
                onClick={() => handleRemoveRoom(room.id)}
                className="remove-button"
              >
                ×
              </button>
            )}
          </div>

          {isLoading && <LoaderDotted />}

          <div className={isLoading ? 'loading-overlay' : ''}>
            <div className="section-label">
              Room Details
            </div>

            <div className="form-row">
              <div className="form-group" data-validation-field={`${room.id}-room_type_id`}>
                <label className="form-label">Room Type</label>
                <select
                  name="room_type_id"
                  className="form-input"
                  value={room.room_type_id}
                  disabled={!canEditRoomAssignment}
                  onChange={(e) => {
                    handleRoomFieldChange(room.id, 'room_type_id', +e.target.value);
                  }}
                >
                  <option value={0}>Select</option>
                  {roomTypeList.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.room_type_name}
                    </option>
                  ))}
                </select>
                {roomErrors.room_type_id && (
                  <p className="validation-error">{roomErrors.room_type_id}</p>
                )}
              </div>

              <div className="form-group" data-validation-field={`${room.id}-room_ids`}>
                <label className="form-label">Select Room</label>
                <div
                  ref={(el) => {
                    if (el) dropdownRefs.current[room.id] = el;
                  }}
                  className="custom-dropdown-container"
                >
                  <div className="dropdown-input-wrapper">
                    <div
                      className="dropdown-input"
                      onClick={() => room.room_type_id && canEditRoomAssignment && setDropdownStates(prev => ({
                        ...prev,
                        [room.id]: !prev[room.id],
                      }))}
                      style={{
                        cursor: room.room_type_id && canEditRoomAssignment ? 'pointer' : 'not-allowed',
                        opacity: room.room_type_id && canEditRoomAssignment ? 1 : 0.5,
                      }}
                    >
                      <div className="selected-rooms-container">
                        {selectedRoomNames.length > 0 ? (
                          selectedRoomNames.map((roomName, idx) => (
                            <div key={idx} className="room-chip-selected">
                              <span>{roomName}</span>
                            </div>
                          ))
                        ) : (
                          <span className="placeholder-text">Select...</span>
                        )}
                      </div>

                      <div className="dropdown-icons">
                        {room.room_ids.length > 0 && canEditRoomAssignment && (
                          <button
                            type="button"
                            className="clear-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClearAll();
                            }}
                            title="Clear all selections"
                          >
                            <ClearIcon style={{ fontSize: '18px' }} />
                          </button>
                        )}
                        <span className={`arrow-icon ${isDropdownOpen ? 'open' : ''}`}>
                          ▼
                        </span>
                      </div>
                    </div>

                    {isDropdownOpen && room.room_type_id && canEditRoomAssignment && (
                      <div className="dropdown-menu">
                        <div className="dropdown-search">
                          <input
                            type="text"
                            placeholder="Search..."
                            className="search-input"
                            value={searchTerm}
                            onChange={(e) => setSearchTerms(prev => ({
                              ...prev,
                              [room.id]: e.target.value,
                            }))}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>

                        <div className="dropdown-options">
                          {filteredRooms.length > 0 ? (
                            filteredRooms.map((availRoom) => (
                              <div
                                key={availRoom.id}
                                className={`dropdown-option ${
                                  room.room_ids.includes(availRoom.id) ? 'selected' : ''
                                }`}
                                onClick={() => handleRoomSelection(availRoom.id)}
                              >
                                <input
                                  type="checkbox"
                                  checked={room.room_ids.includes(availRoom.id)}
                                  onChange={(e) => {
                                    e.preventDefault();
                                    handleRoomSelection(availRoom.id);
                                  }}
                                  className="option-checkbox"
                                  style={{ pointerEvents: 'none' }}
                                />
                                <label className="option-label">{getRoomDisplayName(availRoom.id)}</label>
                              </div>
                            ))
                          ) : (
                            <div className="no-results">
                              No rooms found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {roomErrors.room_ids && (
                  <p className="validation-error">{roomErrors.room_ids}</p>
                )}
                {/* {room.room_ids.length > 0 && (
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                    Selected: {selectedRoomNames.join(", ")}
                  </p>
                )} */}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group" data-validation-field={`${room.id}-rate_plan_id`}>
                <label className="form-label">Rate Type</label>
                <select
                  name="rate_plan_id"
                  className="form-input"
                  value={room.rate_plan_id}
                  onChange={(e) => handleRoomFieldChange(room.id, 'rate_plan_id', +e.target.value)}
                >
                  <option value={0}>Select</option>
                  {ratePlans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {`${plan.name} : ${plan.included}`}
                    </option>
                  ))}
                </select>
                {roomErrors.rate_plan_id && (
                  <p className="validation-error">{roomErrors.rate_plan_id}</p>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Number Of Rooms</label>
                <input
                  type="number"
                  name="no_of_rooms"
                  className="form-input"
                  value={room.no_of_rooms}
                  disabled
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                />
                {/* <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Auto-calculated from selected rooms
                </p> */}
              </div>
            </div>

            <div className="section-label">
              Guest Details
            </div>

            <div className="form-row">
              <div className="form-group" data-validation-field={`${room.id}-adults_per_room`}>
                <label className="form-label">Adult</label>
                <select
                  name="adults_per_room"
                  className="form-input"
                  value={room.adults_per_room}
                  onChange={(e) => handleRoomFieldChange(room.id, 'adults_per_room', +e.target.value)}
                >
                  <option value={0}>Select</option>
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                  <option value={3}>3</option>
                </select>
                {roomErrors.adults_per_room && (
                  <p className="validation-error">{roomErrors.adults_per_room}</p>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Child</label>
                <select
                  name="children"
                  className="form-input"
                  value={room.children}
                  onChange={(e) => handleRoomFieldChange(room.id, 'children', +e.target.value)}
                >
                  <option value={0}>0</option>
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group" data-validation-field={`${room.id}-first_name`}>
                <label className="form-label">First Name</label>
                <input
                  type="text"
                  name="first_name"
                  placeholder="Guest"
                  className="form-input"
                  value={room.first_name}
                  onChange={(e) => handleRoomFieldChange(room.id, 'first_name', e.target.value)}
                />
                {roomErrors.first_name && (
                  <p className="validation-error">{roomErrors.first_name}</p>
                )}
              </div>
              <div className="form-group" data-validation-field={`${room.id}-last_name`}>
                <label className="form-label">Last Name</label>
                <input
                  type="text"
                  name="last_name"
                  placeholder="Guest"
                  className="form-input"
                  value={room.last_name}
                  onChange={(e) => handleRoomFieldChange(room.id, 'last_name', e.target.value)}
                />
                {roomErrors.last_name && (
                  <p className="validation-error">{roomErrors.last_name}</p>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group" data-validation-field={`${room.id}-email`}>
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="guest@example.com"
                  className="form-input"
                  value={room.email}
                  onChange={(e) => handleRoomFieldChange(room.id, 'email', e.target.value)}
                />
                {roomErrors.email && (
                  <p className="validation-error">{roomErrors.email}</p>
                )}
              </div>
              <div className="form-group" data-validation-field={`${room.id}-mobile`}>
                <label className="form-label">Mobile</label>
                <input
                  type="tel"
                  name="mobile"
                  placeholder="Enter Mobile Number"
                  className="form-input"
                  value={room.mobile}
                  onChange={(e) => handleRoomFieldChange(room.id, 'mobile', e.target.value.replace(/\D/g, '').slice(0, 10))}
                />
                {roomErrors.mobile && (
                  <p className="validation-error">{roomErrors.mobile}</p>
                )}
              </div>
            </div>

            <div className="form-group reverse-calc-toggle">
              <label className="reverse-calc-label">
                <input
                  type="checkbox"
                  checked={!!room.use_reverse_price}
                  onChange={(e) => handleReversePriceToggle(room.id, e.target.checked)}
                />
                <span>Prices Include Tax</span>
              </label>
              {/* <p className="reverse-calc-hint">
                Enter final price and base price will be calculated after tax (5% or 18%)
              </p> */}
            </div>

            <div className="form-row price-fields-row">
              <div className="form-group" data-validation-field={`${room.id}-total_price`}>
                <label className="form-label">Price (Base)</label>
                <input
                  type="number"
                  name="total_price"
                  placeholder="3000"
                  className={`form-input ${room.use_reverse_price ? "form-input-readonly" : ""}`}
                  value={room.total_price || room.price}
                  readOnly={!!room.use_reverse_price}
                  disabled={!!room.use_reverse_price}
                  onChange={(e) => {
                    if (room.use_reverse_price) return;
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) {
                      handleRoomFieldChange(room.id, 'total_price', value);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (!room.use_reverse_price && ["-", "+", ".", "e", "E"].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  min="0"
                  step="1"
                />
                {roomErrors.total_price && (
                  <p className="validation-error">{roomErrors.total_price}</p>
                )}
                {/* <p className="field-hint">
                  {room.use_reverse_price
                    ? `Auto-calculated with ${room.tax_amount > 0 && Number(room.total_price || room.price) > 0
                      ? Math.round((room.tax_amount / Number(room.total_price || room.price)) * 100)
                      : 0}% tax`
                    : "Editable - Final price updates automatically"}
                </p> */}
              </div>
              <div className="form-group" data-validation-field={`${room.id}-final_price`}>
                <label className="form-label">Final Price (incl. tax & svc. charge)</label>
                <input
                  type="number"
                  name="final_price"
                  placeholder="3150"
                  className={`form-input ${!room.use_reverse_price ? "form-input-readonly" : ""}`}
                  value={room.final_price}
                  readOnly={!room.use_reverse_price}
                  disabled={!room.use_reverse_price}
                  onChange={(e) => {
                    if (!room.use_reverse_price) return;
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) {
                      handleRoomFieldChange(room.id, 'final_price', value);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (room.use_reverse_price && ["-", "+", ".", "e", "E"].includes(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  min="0"
                  step="1"
                />
                {roomErrors.final_price && (
                  <p className="validation-error">{roomErrors.final_price}</p>
                )}
                {/* <p className="field-hint">
                  {room.use_reverse_price
                    ? "Editable - Base price updates automatically"
                    : `Auto-calculated with ${room.tax_amount > 0 && Number(room.price) > 0
                      ? Math.round((room.tax_amount / Number(room.price)) * 100)
                      : 0}% tax`}
                </p> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isFetching) {
    return (
      <div className="edit-group-reservation-container">
        <LoaderDotted />
      </div>
    );
  }

  return (
    <div className="edit-group-reservation-container">
      {/* Header Section with Group Booking Info */}
      {/* {groupBookingInfo && (
        <div className="booking-info-header">
          <div className="booking-info-card">
            <div className="info-item">
              <span className="info-label">Group Booking ID</span>
              <span className="info-value">{groupBookingInfo.group_booking_id}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Main Booking ID</span>
              <span className="info-value">{groupBookingInfo.main_booking_id}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Main Guest ID</span>
              <span className="info-value">{groupBookingInfo.main_guest_id}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Hotel Code</span>
              <span className="info-value">{groupBookingInfo.hotel_code}</span>
            </div>
          </div>
        </div>
      )} */}

      {/* Date Section */}
      <div className="date-section">
        <div className="date-label">Check-in : Check-out</div>
        <div className="date-input">
          <DateRangeComponent
            range={range}
            onSetRange={handleDateRangeChange}
            isShowPastDate={false}
            startFalse={hasCheckedInRoom}
          />
        </div>
      </div>

      {/* Rooms Container */}
      <div className="rooms-container">
        {rooms.length > 0 ? (
          <>
            {rooms.map((room, index) => renderRoomForm(room, index))}

            <button onClick={handleAddRoom} className="edit-add-room-button">
              <span className="add-room-icon">
                <AddCircleOutlineIcon style={{ fontSize: '20px' }} />
              </span>
              <span>Add Another Room Type</span>
            </button>

            <div className="actions-container">
              <button
                onClick={() => handleSaveChanges()}
                className="action-button save"
                disabled={isLoading}
              >
                {isLoading ? "Processing..." : "Update"}
              </button>
              <button
                onClick={handleCancelReservation}
                className="action-button cancel"
                disabled={
                  isLoading ||
                  !rooms.some(
                    room => !room.is_new_booking && canCancelBookingStatus(room.booking_status)
                  )
                }
              >
                Cancel Reservation
              </button>
            </div>
            <div className="actions-container status-actions-container">
              {isEqualOrAfter(moment().startOf("day"), moment(range.startDate).startOf("day")) &&
                rooms.some(room => canCheckInBookingStatus(room.booking_status)) && (
                <button
                  onClick={() => handleSaveChanges("check_in")}
                  className="action-button check-in"
                  disabled={isLoading}
                >
                  Check In
                </button>
              )}
              {/* <button
                onClick={() => handleSaveChanges("no_show")}
                className="action-button no-show"
                disabled={isLoading}
              >
                No Show
              </button> */}
            </div>
          </>
        ) : (
          <div className="no-data-message">
            <p>No room data found for this group booking.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditGroupReservation;
